import { useState, useEffect, ReactNode, useRef, useCallback } from "react";
import { cn } from "@/lib/utils";
import { docSections, docGroups, DocSection } from "./docs-content";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Check,
  PanelLeft,
  Search,
  X,
} from "lucide-react";
import { MDXProvider } from "@mdx-js/react";
import { SiteNavbar, ThemeToggle } from "@/components/SiteNavbar";
import {
  Conversation,
  TabbedConversation,
  ConversationTab,
  AppWidget,
  AppWidgetList,
  AppWidgetImage,
  AppWidgetTrackList,
  AppWidgetCart,
  AppWidgetConfirmation,
} from "@/client-integrations/Conversation";
import { Diagram } from "@/components/Diagram";
import { SiteLink } from "@/components/SiteLink";
import { DocsSearch } from "./DocsSearch";

// Base URL for docs (update when moving to docs.taias.xyz)
const DOCS_BASE_URL = "https://taias.xyz";

// CodeBlock component with copy button that stays fixed during horizontal scroll
function CodeBlock({ children, ...props }: { children?: ReactNode }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Get the pre element from the event target's parent
    const wrapper = (e.currentTarget as HTMLElement).parentElement;
    const pre = wrapper?.querySelector("pre");
    const text = pre?.textContent || "";

    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="code-block-wrapper relative group my-5">
      <pre
        {...props}
        className="rounded-lg overflow-hidden p-4 overflow-x-auto text-[13px] leading-relaxed font-mono text-zinc-800 dark:text-zinc-200 bg-[#F9F8F7] dark:bg-zinc-800"
      >
        {children}
      </pre>
      <button
        onClick={handleCopy}
        className="absolute right-3 top-3 z-10 p-1.5 rounded-md bg-zinc-200 hover:bg-zinc-300 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200 opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label="Copy code"
      >
        {copied ? (
          <Check className="h-4 w-4 text-green-500 dark:text-green-400" />
        ) : (
          <Copy className="h-4 w-4" />
        )}
      </button>
    </div>
  );
}

// Callout component for info boxes
function Callout({
  children,
  variant = "info",
}: {
  children?: ReactNode;
  variant?: "info" | "warning";
}) {
  return (
    <div
      className={cn(
        "my-5 flex items-start gap-3 rounded-lg border p-4",
        variant === "warning"
          ? "border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20"
          : "border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/50",
      )}
    >
      <span
        className={cn(
          "text-[14px] leading-[1.7] shrink-0",
          variant === "warning"
            ? "text-amber-500"
            : "text-zinc-400 dark:text-zinc-500",
        )}
      >
        {variant === "warning" ? "⚠" : "ⓘ"}
      </span>
      <div className="text-[14px] leading-[1.7] text-zinc-600 dark:text-zinc-400 [&_p]:!m-0 [&_a]:underline [&_a]:underline-offset-2">
        {children}
      </div>
    </div>
  );
}

// Cards container component
function Cards({ children }: { children?: ReactNode }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 my-6">
      {children}
    </div>
  );
}

// Individual Card component for navigation links
function Card({
  title,
  href,
  children,
}: {
  title: string;
  href: string;
  children?: ReactNode;
}) {
  // Convert href like "/quickstart" to hash navigation
  const hashHref = href.startsWith("/") ? `#${href.slice(1)}` : href;

  return (
    <a
      href={hashHref}
      className="group block p-4 rounded-lg border border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-900/50 transition-all no-underline"
    >
      <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 group-hover:text-zinc-700 dark:group-hover:text-white flex items-center gap-1">
        {title}
        <ChevronRight className="h-3.5 w-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
      </span>
      {children && (
        <span className="mt-1 text-xs text-zinc-500 dark:text-zinc-400 block [&>p]:m-0">
          {children}
        </span>
      )}
    </a>
  );
}

// MDX components override
const mdxComponents = {
  pre: CodeBlock,
  Callout,
  Cards,
  Card,
  Conversation,
  TabbedConversation,
  ConversationTab,
  Diagram,
  AppWidget,
  AppWidgetList,
  AppWidgetImage,
  AppWidgetTrackList,
  AppWidgetCart,
  AppWidgetConfirmation,
};

// Left Sidebar navigation - now inline with content, not flush with viewport
function DocsSidebar({
  activeSection,
  onSectionChange,
  isCollapsed,
}: {
  activeSection: string;
  onSectionChange: (id: string) => void;
  isCollapsed: boolean;
}) {
  return (
    <aside
      className={cn(
        "hidden lg:block shrink-0 transition-all duration-300 ease-in-out overflow-x-clip",
        isCollapsed ? "w-0 opacity-0" : "w-56 opacity-100",
      )}
    >
      <div className="sticky top-[73px] w-56">
        <ScrollArea className="h-[calc(100vh-73px)] pr-4">
          <nav className="pt-16 pb-6">
            {docGroups.map((group, groupIdx) => (
              <div key={group.label} className={groupIdx > 0 ? "mt-6" : ""}>
                <h4 className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
                  {group.label}
                </h4>
                <ul className="space-y-0.5">
                  {group.sections.map((section) => (
                    <li key={section.id}>
                      <button
                        onClick={() => onSectionChange(section.id)}
                        className={cn(
                          "w-full text-left px-3 py-2 rounded-lg text-[13px] transition-all duration-150",
                          activeSection === section.id
                            ? "bg-zinc-200 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 font-medium"
                            : "text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800/50 hover:text-zinc-900 dark:hover:text-zinc-100",
                        )}
                      >
                        {section.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </ScrollArea>
      </div>
    </aside>
  );
}

// Right sidebar with ToC and actions
function DocsRightSidebar({
  section,
  activeHeadingId,
}: {
  section: DocSection;
  activeHeadingId: string | null;
}) {
  const [copiedMarkdown, setCopiedMarkdown] = useState(false);
  const tocNavRef = useRef<HTMLElement>(null);

  // Auto-scroll the active TOC item into view within the scroll container
  useEffect(() => {
    if (!activeHeadingId || !tocNavRef.current) return;
    const activeButton = tocNavRef.current.querySelector(
      `[data-heading-id="${activeHeadingId}"]`,
    );
    if (activeButton) {
      activeButton.scrollIntoView({ block: "nearest" });
    }
  }, [activeHeadingId]);

  const handleCopyMarkdown = async () => {
    await navigator.clipboard.writeText(section.rawContent);
    setCopiedMarkdown(true);
    setTimeout(() => setCopiedMarkdown(false), 2000);
  };

  const handleScrollToHeading = (headingId: string) => {
    const element = document.getElementById(headingId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState(null, "", `#${section.id}/${headingId}`);
    }
  };

  const openInChatGPT = () => {
    const message = `Read ${DOCS_BASE_URL}/llms-full.txt - I'm on the ${section.title} page and have questions about Taias.`;
    const url = `https://chatgpt.com/?hints=search&q=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  const openInClaude = () => {
    const message = `Read ${DOCS_BASE_URL}/llms-full.txt - I'm on the ${section.title} page and have questions about Taias.`;
    const url = `https://claude.ai/new?q=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  // Hidden for now until MCP server is ready
  const SHOW_CONNECT_MCP = false;

  return (
    <aside className="hidden xl:block w-52 shrink-0">
      <div className="sticky top-[137px] max-h-[calc(100vh-137px)] flex flex-col pb-6">
        {/* Table of Contents */}
        {section.headings.length > 0 && (
          <div className="min-h-0 flex-1 flex flex-col pb-4">
            <h4 className="text-[13px] font-medium text-zinc-900 dark:text-zinc-100 mb-3 shrink-0">
              On this page
            </h4>
            <nav
              ref={tocNavRef}
              className="flex-1 min-h-0 overflow-y-auto toc-scroll"
            >
              <ul className="space-y-2">
                {section.headings.map((heading) => (
                  <li key={heading.id}>
                    <button
                      data-heading-id={heading.id}
                      onClick={() => handleScrollToHeading(heading.id)}
                      className={cn(
                        "text-left text-[13px] leading-normal transition-colors w-full cursor-pointer",
                        heading.level === 3 && "pl-3",
                        activeHeadingId === heading.id
                          ? "text-zinc-900 dark:text-zinc-100"
                          : "text-zinc-500 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300",
                      )}
                    >
                      {heading.text}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-zinc-200 dark:border-zinc-800 pt-4 shrink-0" />

        {/* Actions */}
        <div className="space-y-2">
          <button
            onClick={handleCopyMarkdown}
            className="flex items-center gap-2.5 text-[13px] text-zinc-500 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors w-full py-0.5 cursor-pointer"
          >
            {copiedMarkdown ? (
              <Check className="h-4 w-4 text-green-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            <span>{copiedMarkdown ? "Copied!" : "Copy Markdown"}</span>
          </button>

          <button
            onClick={openInChatGPT}
            className="flex items-center gap-2.5 text-[13px] text-zinc-500 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors w-full py-0.5 cursor-pointer"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.896zm16.597 3.855-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08-4.778 2.758a.795.795 0 0 0-.393.681zm1.097-2.365 2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" />
            </svg>
            <span>Open in ChatGPT</span>
          </button>

          <button
            onClick={openInClaude}
            className="flex items-center gap-2.5 text-[13px] text-zinc-500 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors w-full py-0.5 cursor-pointer"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 1200 1200"
              fill="currentColor"
            >
              <path d="M234 800l235-132 4-11-4-7h-11l-39-2-134-4-117-5-112-6-29-6-26-35 3-17 23-16 34 3 76 5 113 8 82 5 122 12h19l3-8-7-4-5-5-117-79-127-84-66-48-36-25-18-23-8-50 33-36 44 3 11 3 44 34 95 73 124 91 18 15 7-5 1-4-8-13-68-122-72-124-31-51-9-31c-3-13-5-23-5-36l37-50 21-7 49 7 21 18 31 70 50 111 77 150 23 45 12 41 5 13h8v-7l6-85 12-104 11-134 4-38 19-45 37-24 29 14 24 34-3 22-14 92-28 145-18 96h11l12-12 49-65 82-103 36-40 42-45 27-22h52l37 57-17 58-52 67-44 57-63 84-39 68 4 5 9-1 142-30 77-14 91-16 42 19 4 20-16 40-98 24-115 23-170 40-3 2 3 3 77 7 33 2h81l150 11 39 26 24 32-4 24-61 31-81-19-190-46-65-16h-9v6l20 32 117 175 6 54-8 17-31 11-33-6-68-96-71-108-56-96-7 4-34 361-15 18-37 14-30-23-16-37 16-74 19-95 16-76 14-95 8-31v-2l-7 1-71 98-109 146-85 92-21 8-35-18 3-37 20-29 119-151 71-93 47-54v-8h-3L205 929l-56 8-24-23 3-37 11-12 95-65z" />
            </svg>
            <span>Open in Claude</span>
          </button>

          {SHOW_CONNECT_MCP && (
            <a
              href="#connect-mcp"
              className="flex items-center gap-2.5 text-[13px] text-zinc-500 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors w-full py-0.5 cursor-pointer"
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="currentColor"
                fillRule="evenodd"
              >
                <path d="M15.688 2.343a2.588 2.588 0 00-3.61 0l-9.626 9.44a.863.863 0 01-1.203 0 .823.823 0 010-1.18l9.626-9.44a4.313 4.313 0 016.016 0 4.116 4.116 0 011.204 3.54 4.3 4.3 0 013.609 1.18l.05.05a4.115 4.115 0 010 5.9l-8.706 8.537a.274.274 0 000 .393l1.788 1.754a.823.823 0 010 1.18.863.863 0 01-1.203 0l-1.788-1.753a1.92 1.92 0 010-2.754l8.706-8.538a2.47 2.47 0 000-3.54l-.05-.049a2.588 2.588 0 00-3.607-.003l-7.172 7.034-.002.002-.098.097a.863.863 0 01-1.204 0 .823.823 0 010-1.18l7.273-7.133a2.47 2.47 0 00-.003-3.537z" />
                <path d="M14.485 4.703a.823.823 0 000-1.18.863.863 0 00-1.204 0l-7.119 6.982a4.115 4.115 0 000 5.9 4.314 4.314 0 006.016 0l7.12-6.982a.823.823 0 000-1.18.863.863 0 00-1.204 0l-7.119 6.982a2.588 2.588 0 01-3.61 0 2.47 2.47 0 010-3.54l7.12-6.982z" />
              </svg>
              <span>Connect MCP</span>
            </a>
          )}
        </div>
      </div>
    </aside>
  );
}

/**
 * Parse a compound hash like "types/flow-definition" into section and heading parts.
 */
function parseDocHash(hash: string): {
  sectionId: string;
  headingId?: string;
} {
  const slashIndex = hash.indexOf("/");
  if (slashIndex === -1) {
    return { sectionId: hash };
  }
  return {
    sectionId: hash.slice(0, slashIndex),
    headingId: hash.slice(slashIndex + 1) || undefined,
  };
}

export function DocsPage() {
  const pendingHeadingRef = useRef<string | null>(null);
  const activeSectionRef = useRef<string>("");
  const isInitialLoadRef = useRef(true);

  const [activeSection, setActiveSection] = useState(() => {
    const hash = window.location.hash.slice(1);
    const { sectionId, headingId } = parseDocHash(hash);
    const section = docSections.find((s) => s.id === sectionId);
    if (section) {
      if (headingId) pendingHeadingRef.current = headingId;
      activeSectionRef.current = section.id;
      return section.id;
    }
    activeSectionRef.current = docSections[0].id;
    return docSections[0].id;
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeHeadingId, setActiveHeadingId] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Keyboard shortcut for search (⌘K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Handle search navigation
  const handleSearchNavigate = useCallback(
    (sectionId: string, headingId?: string) => {
      if (headingId && sectionId === activeSectionRef.current) {
        // Same section — just scroll to heading
        const el = document.getElementById(headingId);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        window.history.replaceState(null, "", `#${sectionId}/${headingId}`);
      } else {
        if (headingId) pendingHeadingRef.current = headingId;
        setActiveSection(sectionId);
      }
    },
    [],
  );

  // Update URL hash and handle navigation when section changes
  useEffect(() => {
    activeSectionRef.current = activeSection;
    const headingId = pendingHeadingRef.current;
    pendingHeadingRef.current = null;

    const hashValue = headingId
      ? `${activeSection}/${headingId}`
      : activeSection;

    if (isInitialLoadRef.current) {
      window.history.replaceState(null, "", `#${hashValue}`);
      isInitialLoadRef.current = false;
    } else {
      window.history.pushState(null, "", `#${hashValue}`);
    }

    if (headingId) {
      // Wait for section to render, then scroll to heading
      setTimeout(() => {
        const el = document.getElementById(headingId);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }

    setActiveHeadingId(null);
  }, [activeSection]);

  // Listen for hash changes (from <a> tags) and popstate (browser back/forward)
  useEffect(() => {
    const handleNavigation = () => {
      const hash = window.location.hash.slice(1);
      if (!hash) return;
      const { sectionId, headingId } = parseDocHash(hash);
      const section = docSections.find((s) => s.id === sectionId);
      if (!section) return;

      if (section.id === activeSectionRef.current) {
        // Same section — just scroll to heading if present
        if (headingId) {
          const el = document.getElementById(headingId);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      } else {
        // Different section
        if (headingId) pendingHeadingRef.current = headingId;
        setActiveSection(section.id);
      }
    };

    window.addEventListener("hashchange", handleNavigation);
    window.addEventListener("popstate", handleNavigation);
    return () => {
      window.removeEventListener("hashchange", handleNavigation);
      window.removeEventListener("popstate", handleNavigation);
    };
  }, []);

  // Scroll tracking for ToC highlighting
  const currentSection = docSections.find((s) => s.id === activeSection);

  const handleScroll = useCallback(() => {
    if (!currentSection || currentSection.headings.length === 0) return;

    const headingElements = currentSection.headings
      .map((h) => ({
        id: h.id,
        element: document.getElementById(h.id),
      }))
      .filter((h) => h.element !== null);

    if (headingElements.length === 0) return;

    // Find the heading that's currently in view (closest to top)
    const scrollTop = window.scrollY;
    const offset = 100; // Account for sticky header

    let currentHeading = headingElements[0].id;

    for (const { id, element } of headingElements) {
      if (element && element.offsetTop - offset <= scrollTop) {
        currentHeading = id;
      }
    }

    setActiveHeadingId(currentHeading);
  }, [currentSection]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    // Initial check
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const currentIndex = docSections.findIndex((s) => s.id === activeSection);
  const prevSection = currentIndex > 0 ? docSections[currentIndex - 1] : null;
  const nextSection =
    currentIndex < docSections.length - 1
      ? docSections[currentIndex + 1]
      : null;

  // Right content for docs navbar: Search + Ask AI + Theme toggle
  const docsRightContent = (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setSearchOpen(true)}
        className="hidden sm:flex items-center gap-2 text-zinc-500 dark:text-zinc-400 border-zinc-200 dark:border-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-800"
      >
        <Search className="h-4 w-4" />
        <span className="text-sm">Search Documentation</span>
        <kbd className="hidden md:inline-flex ml-2 pointer-events-none h-5 select-none items-center gap-1 rounded border border-zinc-200 dark:border-zinc-700 bg-zinc-100 dark:bg-zinc-800 px-1.5 font-mono text-[10px] font-medium text-zinc-600 dark:text-zinc-400">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>
      <ThemeToggle />
    </div>
  );

  // Mobile sidebar overlay and content
  const mobileSidebarOverlay = mobileMenuOpen && (
    <div
      className="fixed inset-0 bg-black/50 z-40 lg:hidden"
      onClick={() => setMobileMenuOpen(false)}
    />
  );

  const mobileSidebar = (
    <aside
      className={cn(
        "fixed top-0 left-0 z-50 h-screen w-64 bg-white dark:bg-zinc-950 border-r border-zinc-200 dark:border-zinc-800 transform transition-transform lg:hidden",
        mobileMenuOpen ? "translate-x-0" : "-translate-x-full",
      )}
    >
      <div className="flex items-center justify-between px-4 py-4 border-b border-zinc-200 dark:border-zinc-800">
        <SiteLink href="/">
          <img
            src="/taias_light.png"
            alt="Taias"
            className="h-7 w-auto dark:hidden"
          />
          <img
            src="/taias_dark.png"
            alt="Taias"
            className="h-7 w-auto hidden dark:block"
          />
        </SiteLink>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(false)}
        >
          <X className="h-5 w-5" />
        </Button>
      </div>
      <ScrollArea className="h-[calc(100vh-65px)]">
        <nav className="p-3">
          {docGroups.map((group, groupIdx) => (
            <div key={group.label} className={groupIdx > 0 ? "mt-5" : ""}>
              <h4 className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
                {group.label}
              </h4>
              <ul className="space-y-0.5">
                {group.sections.map((section) => (
                  <li key={section.id}>
                    <button
                      onClick={() => {
                        setActiveSection(section.id);
                        setMobileMenuOpen(false);
                      }}
                      className={cn(
                        "w-full text-left px-3 py-2 rounded-lg text-[13px] transition-all duration-150",
                        activeSection === section.id
                          ? "bg-zinc-200 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 font-medium"
                          : "text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800/50 hover:text-zinc-900 dark:hover:text-zinc-100",
                      )}
                    >
                      {section.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </ScrollArea>
    </aside>
  );

  // Mobile menu button for navbar
  const mobileMenuButton = (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => setMobileMenuOpen(true)}
      className="lg:hidden text-zinc-600 dark:text-zinc-400"
    >
      <PanelLeft className="h-5 w-5" />
    </Button>
  );

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950">
      {/* Mobile sidebar overlay */}
      {mobileSidebarOverlay}
      {mobileSidebar}

      {/* Search Dialog */}
      <DocsSearch
        open={searchOpen}
        onOpenChange={setSearchOpen}
        onNavigate={handleSearchNavigate}
      />

      {/* Shared Navbar */}
      <SiteNavbar
        variant="standard"
        rightContent={docsRightContent}
        sidebarToggle={mobileMenuButton}
      />

      {/* Main layout with sidebar inside content boundaries */}
      <div className="mx-auto max-w-7xl px-6">
        {/* Content layout - three column with gaps */}
        <div className="flex gap-8">
          {/* Left Sidebar - fixed width */}
          <DocsSidebar
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            isCollapsed={sidebarCollapsed}
          />

          {/* Main content area - expands when sidebar collapses */}
          <main className="flex-1 min-w-0 transition-all duration-300 ease-in-out">
            {/* Toggle button - always above content H1, aligned to left edge */}
            <div className="hidden lg:block pt-4 pb-4 -ml-1">
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                title={sidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
              >
                <PanelLeft className="h-5 w-5" />
              </button>
            </div>

            {/* Content and right sidebar */}
            <div className="flex gap-8">
              {/* Main content column - grows to fill available space */}
              <div ref={contentRef} className="flex-1 min-w-0 pb-8">
                <div className="mdx-content">
                  <MDXProvider components={mdxComponents}>
                    {currentSection && <currentSection.Component />}
                  </MDXProvider>
                </div>

                {/* Navigation between sections */}
                <div className="mt-16 pt-8 border-t border-zinc-200 dark:border-zinc-800 grid grid-cols-2 gap-3">
                  {prevSection ? (
                    <button
                      onClick={() => setActiveSection(prevSection.id)}
                      className="group flex flex-col items-start text-left p-3 rounded-lg border border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-900/50 transition-all"
                    >
                      <span className="text-[10px] font-medium text-zinc-400 dark:text-zinc-500 mb-0.5 flex items-center gap-0.5 uppercase tracking-wide">
                        <ChevronLeft className="h-3 w-3" />
                        Previous
                      </span>
                      <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300 group-hover:text-zinc-900 dark:group-hover:text-zinc-100 transition-colors">
                        {prevSection.title}
                      </span>
                    </button>
                  ) : (
                    <div />
                  )}
                  {nextSection ? (
                    <button
                      onClick={() => setActiveSection(nextSection.id)}
                      className="group flex flex-col items-end text-right p-3 rounded-lg border border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-900/50 transition-all"
                    >
                      <span className="text-[10px] font-medium text-zinc-400 dark:text-zinc-500 mb-0.5 flex items-center gap-0.5 uppercase tracking-wide">
                        Next
                        <ChevronRight className="h-3 w-3" />
                      </span>
                      <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300 group-hover:text-zinc-900 dark:group-hover:text-zinc-100 transition-colors">
                        {nextSection.title}
                      </span>
                    </button>
                  ) : (
                    <div />
                  )}
                </div>
              </div>

              {/* Right Sidebar - fixed width, stays in place */}
              {currentSection && (
                <DocsRightSidebar
                  key={currentSection.id}
                  section={currentSection}
                  activeHeadingId={activeHeadingId}
                />
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
